/**
 * The configuration surface (ORDR-119, FR-CFM-004): one collapsed panel in
 * the admin mode, organised by FRS-55 section 55.3's cadence.
 *
 * COLLAPSED, AND NOTHING LOADS UNTIL A MODULE OPENS. The framework's admin
 * route renders host panels at the BOTTOM, one surface at a time (the
 * `GrantsPanel` precedent), and nineteen GETs on page load would spend a fifth
 * of the 100/minute throttle on a panel nobody opened.
 *
 * NOT CAPABILITY-GATED AS A WHOLE (ruling B10). It renders for any acting
 * node; each group decides for itself whether it can be changed, and the
 * server decides every write.
 */

import { useState, type ReactElement } from "react";
import { CADENCE_MODULES, OTHER_SECTION, moduleDomains, type CadenceModule } from "./cadence.js";
import { CadenceSection } from "./CadenceSection.js";
import type { ConfigSurfaceProps } from "./index.js";
import { UnsetGroups } from "./UnsetGroups.js";
import { useGroups } from "./useGroups.js";

/** The modules the "Groups nothing has set" list reports on (item 7). */
const LOCATION_MODULES = CADENCE_MODULES.filter((m) => m.key === "location-setup" || m.key === "location-daily");

export function ConfigSurfaceView({ client, scope }: ConfigSurfaceProps): ReactElement {
  const groups = useGroups(client, scope.nodeId);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());

  const open = (module: CadenceModule): void => {
    groups.load(moduleDomains(module));
    setOpened((prev) => (prev.has(module.key) ? prev : new Set([...prev, module.key])));
  };

  return (
    <details data-testid="config-surface" className="mt-6 rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)]">
      <summary className="cursor-pointer p-3 text-sm font-medium">Settings for {scope.nodeName}</summary>
      <div className="flex flex-col gap-3 border-t border-[var(--color-neutral-200)] p-3">
        {[...CADENCE_MODULES, OTHER_SECTION].map((module) => (
          <CadenceSection key={module.key} module={module} groups={groups} scope={scope} onOpen={open} />
        ))}
        <UnsetGroups modules={LOCATION_MODULES} opened={opened} groups={groups} nodeName={scope.nodeName} />
      </div>
    </details>
  );
}
